// Rock create form. Title, "why this matters", owner + team, quarter +
// status (Draft / Committed), inline milestones, and a "raised from
// opportunity" link. Replicates the Drillcut RockForm exactly,
// re-skinned to BSP tokens.

import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import type { CreateSeed } from "./types";
import {
  FormSection,
  FormLabel,
  Hint,
  DC,
  ENTITY,
  twoCol,
  iconBtn,
  inlineInput,
  ghostAdd,
} from "./primitives";
import { DcInput, SegmentedToggle } from "./fields";
import { RichTextEditor } from "../../components/RichTextEditor";
import { PersonSelect, TeamSelect, SelectBtn, OpportunitySelect } from "./selects";
import { Footer } from "./Footer";
import { tpApi } from "../../api";
import { currentQuarter, nextQuarter as nextQ } from "../../utils";

interface MilestoneDraft {
  id: string;
  title: string;
  due: string;
}

interface RockFormProps {
  seed?: CreateSeed;
  onClose: () => void;
  onCreated: () => void;
}

// Build a rolling 4-quarter list anchored on the current quarter.
function quartersOptions(): string[] {
  const list: string[] = [];
  let q = currentQuarter();
  for (let i = 0; i < 4; i++) {
    list.push(q);
    q = nextQ(q);
  }
  return list;
}

function quarterEndDate(quarter: string): Date {
  const [yearStr, qStr] = quarter.split("-Q");
  const year = Number(yearStr);
  const q = Number(qStr);
  const endMonth = q * 3 - 1; // Q1 → month 2 (Mar), Q2 → 5, Q3 → 8, Q4 → 11
  // Last day of that month, end-of-business.
  return new Date(Date.UTC(year, endMonth + 1, 0, 17, 0, 0));
}

function cryptoId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : String(Date.now() + Math.random());
}

function parseDue(raw: string): Date | null {
  const t = raw.trim();
  if (!t) return null;
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), 17, 0, 0));
}

export function RockForm({ seed, onClose, onCreated }: RockFormProps) {
  const [title, setTitle] = useState(seed?.title ?? "");
  const [description, setDescription] = useState("");
  const [owner, setOwner] = useState<string | null>(null);
  const [team, setTeam] = useState<string | null>(null);
  const [quarter, setQuarter] = useState(seed?.quarter ?? currentQuarter());
  const [status, setStatus] = useState<"draft" | "committed">("draft");
  const [milestones, setMilestones] = useState<MilestoneDraft[]>([
    { id: cryptoId(), title: "", due: "" },
    { id: cryptoId(), title: "", due: "" },
  ]);
  const [linkOpp, setLinkOpp] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const canSubmit = title.trim().length > 0 && !pending;
  const filledMilestoneCount = milestones.filter((m) => m.title.trim()).length;
  const summary = `${filledMilestoneCount} milestone${
    filledMilestoneCount === 1 ? "" : "s"
  } · ${quarter}`;

  const setMilestone = (id: string, patch: Partial<MilestoneDraft>) =>
    setMilestones((ms) => ms.map((m) => (m.id === id ? { ...m, ...patch } : m)));

  async function submit() {
    setSubmitError(null);
    setPending(true);
    try {
      const rock = await tpApi.createRock({
        title: title.trim(),
        description:
          description.trim() === "<p></p>" || description.trim() === ""
            ? undefined
            : description.trim(),
        quarter,
        dueAt: quarterEndDate(quarter).toISOString(),
        status: status === "committed" ? "on_track" : "draft",
        parentTeamId: team ?? undefined,
        fromIssueId: linkOpp ?? undefined,
      });
      // Re-point the primary owner if the user picked someone else.
      // Server seeds the creator on insert; setPrimaryOwner downgrades
      // the previous primary to co_owner.
      if (owner) {
        await tpApi.setPrimaryOwner("rock", rock.id, { subjectType: "user", subjectId: owner });
      }
      // Seed any filled milestone drafts. Empty rows are ignored.
      for (const m of milestones) {
        if (!m.title.trim()) continue;
        const due = parseDue(m.due) ?? quarterEndDate(quarter);
        await tpApi.createMilestone({
          rockId: rock.id,
          title: m.title.trim(),
          dueAt: due.toISOString(),
        });
      }
      // Mark the source opportunity as converted so its lifecycle
      // matches the new committed rock — the server's convert-to-rock
      // endpoint does this for the direct flow; for an explicit-link
      // here we mirror the side-effect manually.
      if (linkOpp) {
        await tpApi.setIssueStatus(linkOpp, "converted").catch(() => undefined);
      }
      onCreated();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Unknown error.");
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <div style={bodyScrollStyle}>
        <FormSection>
          <FormLabel required>Rock title</FormLabel>
          <DcInput
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Roll out new CRM across pilot team"
          />
          <Hint>Big enough to warrant a quarter of focus — not a weekly task.</Hint>
        </FormSection>

        <FormSection>
          <FormLabel>Why this matters</FormLabel>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            placeholder="A couple of sentences framing the outcome."
            minRows={3}
          />
        </FormSection>

        <div style={twoCol}>
          <FormSection>
            <FormLabel required>Owner</FormLabel>
            <PersonSelect value={owner} onChange={setOwner} />
          </FormSection>
          <FormSection>
            <FormLabel>Team</FormLabel>
            <TeamSelect value={team} onChange={setTeam} />
          </FormSection>
        </div>

        <div style={twoCol}>
          <FormSection>
            <FormLabel required>Quarter</FormLabel>
            <SelectBtn
              value={quarter}
              options={quartersOptions()}
              onChange={(v) => setQuarter(v)}
            />
          </FormSection>
          <FormSection>
            <FormLabel>Status</FormLabel>
            <SegmentedToggle
              value={status}
              onChange={(v) => setStatus(v as "draft" | "committed")}
              options={[
                { v: "draft", label: "Draft", hint: "Shape it first" },
                { v: "committed", label: "Committed", hint: "Locked for the Q" },
              ]}
            />
          </FormSection>
        </div>

        <FormSection>
          <FormLabel>Milestones</FormLabel>
          <Hint style={{ marginBottom: 10 }}>
            Break the Rock into 3-5 visible checkpoints. You can add more later.
          </Hint>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {milestones.map((ms, idx) => (
              <div
                key={ms.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "28px 1fr 130px 28px",
                  gap: 8,
                  alignItems: "center",
                  padding: "8px 10px",
                  background: DC.surfaceMuted,
                  border: `1.5px solid ${DC.border}`,
                  borderLeft: `3px solid ${ENTITY.milestone.color}`,
                  borderRadius: 8,
                }}
              >
                <span
                  style={{
                    font: "800 10px/1 var(--bsp-font-sans)",
                    color: DC.grey,
                    textAlign: "center",
                  }}
                >
                  M{idx + 1}
                </span>
                <input
                  value={ms.title}
                  onChange={(e) => setMilestone(ms.id, { title: e.target.value })}
                  placeholder={`Checkpoint ${idx + 1}…`}
                  style={inlineInput}
                />
                <input
                  type="date"
                  value={ms.due}
                  onChange={(e) => setMilestone(ms.id, { due: e.target.value })}
                  style={{ ...inlineInput, fontVariantNumeric: "tabular-nums" }}
                />
                <button
                  type="button"
                  onClick={() =>
                    setMilestones((m) => m.filter((x) => x.id !== ms.id))
                  }
                  style={iconBtn}
                  title="Remove"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            <button
              type="button"
              style={ghostAdd}
              onClick={() =>
                setMilestones((m) => [
                  ...m,
                  { id: cryptoId(), title: "", due: "" },
                ])
              }
            >
              <Plus size={14} /> Add milestone
            </button>
          </div>
        </FormSection>

        <FormSection>
          <FormLabel>Raised from opportunity</FormLabel>
          <OpportunitySelect value={linkOpp} onChange={setLinkOpp} />
          <Hint>
            Optional — keeps the audit trail from CI board to committed Rock.
          </Hint>
        </FormSection>
      </div>

      <Footer
        color="var(--bsp-blue)"
        primary={status === "committed" ? "Commit Rock" : "Save draft"}
        disabled={!canSubmit}
        onCancel={onClose}
        onSubmit={submit}
        error={submitError}
        summary={summary}
      />
    </>
  );
}

const bodyScrollStyle = {
  flex: 1,
  overflowY: "auto" as const,
  padding: "16px 22px 20px",
  display: "flex",
  flexDirection: "column" as const,
};
